/**
 * Book-Now click handler — opens Booking.com in a NEW TAB using the
 * async-popup pattern.
 *
 * 2026-07-14 REGRESSION FIX (post-Bug-A3):
 *   Previous version used a synthetic `<a target="_blank">.click()`
 *   AFTER `await api.post(...)`. Modern browsers strip the
 *   user-gesture flag after the async await, so the synthetic click
 *   silently no-ops (no popup, no navigation, no error). Loader
 *   flashed briefly then nothing happened.
 *
 *   The correct pattern is to OPEN a blank window SYNCHRONOUSLY on
 *   the click (while the user gesture is still fresh), then update
 *   `w.location.href` once the fetch resolves. Chrome/Safari/Firefox
 *   all allow this — the tab shows about:blank for ~300ms then
 *   navigates.
 *
 * Flow:
 *   1. Open a blank _blank window right away (user gesture is fresh).
 *   2. Turn loader on.
 *   3. POST /api/booking-clicks/outbound (may block up to 2s while
 *      the server resolves Booking's dest_id).
 *   4. Turn loader off.
 *   5. Set `blank.location.href = returned target_url`. Done — one
 *      new tab, current tab untouched.
 *   6. If POST errors AND we have a static `booking_url` /
 *      `affiliate_url` fallback → use that in the blank window.
 *      If no fallback → close the blank window and toast the error.
 *
 * Optional args:
 *   • checkIn / checkOut — ISO YYYY-MM-DD, sent only when BOTH set.
 *   • onLoadingChange(bool) — controls `<BookNowFlyingLoader>`.
 */
import { toast } from "sonner";
import { api } from "./api";

const FAILURE_TOAST =
  "Couldn't open Booking.com right now. Please try again in a moment.";

/**
 * Open a blank tab synchronously while the user gesture is fresh.
 * Returns the WindowProxy (or null if a popup blocker fires).
 * Passing `noopener` to `window.open` returns null per spec — we
 * intentionally OMIT it here so we can retain the handle. After
 * navigating we defensively null the opener to mimic `noopener`.
 */
function _openBlank() {
  try {
    return window.open("about:blank", "_blank");
  } catch {
    return null;
  }
}

function _navigateBlank(blank, url) {
  if (!blank) {
    // Popup blocker fired at open time. Best we can do is
    // same-tab redirect so the click isn't a total no-op.
    window.location.href = url;
    return;
  }
  try {
    // Sever opener to prevent target-page tab-nabbing (rel=noopener
    // equivalent). Wrap in try — some browsers freeze opener.
    try { blank.opener = null; } catch (_) { /* noop */ }
    blank.location.href = url;
  } catch {
    // If we can't drive the pre-opened window (extremely rare —
    // e.g. it was closed by the user between open + navigate),
    // fall back to same-tab.
    window.location.href = url;
  }
}

/**
 * Wrap the final CJ URL in a TravelBio `/booking-transit` interstitial
 * so the pre-opened tab shows OUR navy+plane loader instead of
 * Booking's default affiliate landing during the CJ redirect chain
 * (2026-07-14 flash-fix). The transit page reads `?url=` and
 * `window.location.replace()`s to it after a tiny paint tick.
 *
 * Uses the current origin so preview and prod route to their own
 * transit page — no env plumbing needed.
 */
function _wrapWithTransit(rawUrl) {
  if (!rawUrl) return rawUrl;
  try {
    const origin = window.location.origin;
    return `${origin}/booking-transit?url=${encodeURIComponent(rawUrl)}`;
  } catch {
    return rawUrl;
  }
}

function _closeBlank(blank) {
  try { blank && blank.close(); } catch (_) { /* noop */ }
}

export async function bookNow({
  card,
  providerHint = null,
  checkIn = null,
  checkOut = null,
  onLoadingChange = null,
} = {}) {
  const fallback = card?.booking_url || card?.affiliate_url || null;
  const cardId = card?.id || card?.card_id || card?.travelbio_id;

  // Open the blank tab RIGHT NOW while the user gesture is fresh.
  const blank = _openBlank();

  if (!cardId) {
    if (fallback) {
      _navigateBlank(blank, _wrapWithTransit(fallback));
      return { provider: "fallback_no_card_id" };
    }
    _closeBlank(blank);
    console.error("[BookNow] no card_id and no fallback url on card", card);
    toast.error(FAILURE_TOAST);
    return { provider: "error_no_id_no_fallback" };
  }
  const ci = (checkIn || "").trim();
  const co = (checkOut || "").trim();
  const datesReady = ci && co;
  try {
    onLoadingChange && onLoadingChange(true);
    const { data } = await api.post("/booking-clicks/outbound", {
      card_id: cardId,
      name: card?.name || null,
      city: card?.city || card?.location?.city || null,
      state: card?.state || card?.location?.state || null,
      country: card?.country || card?.location?.country || null,
      lat: card?.lat ?? card?.location?.lat ?? null,
      lng: card?.lng ?? card?.location?.lng ?? null,
      ...(datesReady ? { checkin: ci, checkout: co } : {}),
      ...(providerHint ? { provider_hint: providerHint } : {}),
    });
    const url = data?.target_url || data?.redirect_url;
    if (!url) throw new Error("no url in response");
    onLoadingChange && onLoadingChange(false);
    _navigateBlank(blank, _wrapWithTransit(url));
    return data;
  } catch (err) {
    onLoadingChange && onLoadingChange(false);
    console.error("[BookNow] outbound failed", err);
    if (fallback) {
      _navigateBlank(blank, _wrapWithTransit(fallback));
      return { provider: "fallback", error: String(err?.message || err) };
    }
    _closeBlank(blank);
    toast.error(FAILURE_TOAST);
    return { provider: "error", error: String(err?.message || err) };
  }
}
